import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {useInventory} from '../hooks/useInventory';
import {Product} from '../../products/models/product.model';
import {FormField, FormActions} from '../../../Presentation/components/common';
import {useFormState, createFieldProps} from '../../../Presentation/utils/formUtils';
import {AppText, AppTitle, AppCardTitle, AppBodyText, AppLabelText} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import {UnifiedSerialNumberInput} from '../../serialNumbers';
import {Warehouse} from 'lucide-react';
import {useNotification} from '../../../Services/useNotification';
import {LoggerService} from '../../../Services/common/LoggerService';
import {InventoryAPIDataSource} from '../api/InventoryAPIDataSource';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

interface AddStockFormProps {
  product: Product;
  currentUserId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  quantity: string | number;
  serials: string[];
}

interface StockFormData {
  productId: number;
  locationId: number;
  createdBy: number;
  quantity: number;
  serialNumbers?: string[];
}

const MAIN_WAREHOUSE_ID = 0;

const useCurrentStock = (productId: number) => {
  const [currentStock, setCurrentStock] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const {error: showError} = useNotification();

  useEffect(() => {
    const fetchCurrentStock = async () => {
      try {
        setIsLoading(true);
        const inventoryStatus = await InventoryAPIDataSource.getProductInventoryStatus(productId.toString());
        const totalQuantity = inventoryStatus?.[0]?.total_quantity || 0;
        setCurrentStock(totalQuantity);
      } catch (error) {
        setCurrentStock(0);
        // Let StandardizedErrorHandler extract server error message
        StandardizedErrorHandler.getInstance().handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (productId) {
      fetchCurrentStock();
    }
  }, [productId]);

  return {currentStock, isLoading};
};

const createValidationRules = (hasSerial: boolean) => ({
  quantity: {
    required: !hasSerial,
    customValidator: (value: string | number) => {
      if (!hasSerial) {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue <= 0) {
          return 'يجب إدخال كمية صحيحة أكبر من صفر';
        }
      }
      return null;
    }
  },
  serials: {
    required: false,
    customValidator: (value: string[]) => {
      if (hasSerial && value && value.length > 0) {
        const validSerials = value.filter(s => s && s.trim());

        if (validSerials.length === 0) {
          return 'يجب إدخال رقم تسلسلي واحد على الأقل';
        }

        const uniqueSerials = [...new Set(validSerials)];
        if (uniqueSerials.length !== validSerials.length) {
          return 'يوجد تكرار في الأرقام التسلسلية';
        }

        for (const serial of validSerials) {
          if (serial.trim().length < 3) {
            return 'رقم التسلسل يجب أن يكون 3 أحرف على الأقل';
          }
          if (serial.trim().length > 50) {
            return 'رقم التسلسل يجب أن يكون أقل من 50 حرف';
          }
          if (!/^[A-Za-z0-9\-_]+$/.test(serial.trim())) {
            return 'رقم التسلسل يجب أن يحتوي على أحرف وأرقام فقط';
          }
        }
      }
      return null;
    }
  }
});

const validateFormSubmission = (product: Product, currentUserId: number): string | null => {
  if (!product.id || product.id <= 0) {
    return 'معرف المنتج غير صحيح';
  }

  if (!currentUserId || currentUserId <= 0) {
    return 'معرف المستخدم غير صحيح - يرجى إعادة تسجيل الدخول';
  }

  return null;
};

const prepareFormData = (data: FormData, product: Product, currentUserId: number): StockFormData => {
  const baseData: StockFormData = {
    productId: Number(product.id),
    locationId: MAIN_WAREHOUSE_ID,
    createdBy: Number(currentUserId),
    quantity: 0
  };

  if (product.hasSerial) {
    const validSerials = (data.serials || []).filter(s => s && s.trim());
    baseData.serialNumbers = validSerials;
    baseData.quantity = validSerials.length;
  } else {
    baseData.quantity = Number(data.quantity);
  }

  return baseData;
};

const ProductInfoCard: React.FC<{product: Product; currentStock: number; isLoading: boolean}> = ({
  product,
  currentStock,
  isLoading
}) => (
  <Card className="p-4">
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-3">
        <AppCardTitle className="font-semibold text-gray-800">
          {product.barcode}
        </AppCardTitle>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          product.hasSerial
            ? 'bg-brand-100 text-brand-800'
            : 'bg-green-100 text-green-800'
        }`}>
          {product.hasSerial ? 'بسيريال' : 'بدون سيريال'}
        </span>
      </div>
      {!product.hasSerial && (
        <AppBodyText className="text-gray-600">
          المخزون: {isLoading ? '...' : currentStock}
        </AppBodyText>
      )}
    </div>
  </Card>
);

const LocationInfoCard: React.FC = () => (
  <Card className="p-3 bg-brand-50 border-brand-200">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Warehouse className="w-4 h-4 text-brand-600" />
        <AppLabelText className="text-brand-800">
          المستودع الرئيسي
        </AppLabelText>
      </div>
      <div className="px-2 py-1 bg-brand-600 text-white rounded text-xs">
        مؤكد
      </div>
    </div>
  </Card>
);

const AddStockForm: React.FC<AddStockFormProps> = ({
  product,
  currentUserId,
  onSuccess,
  onCancel
}) => {
  const {addStock, isLoading: submitting} = useInventory();
  const {currentStock, isLoading: stockLoading} = useCurrentStock(product.id);
  const {success: showSuccess, error: showError} = useNotification();

  const initialFormData = useMemo(() => ({
    quantity: product.hasSerial ? 0 : '',
    serials: product.hasSerial ? [''] : [] as string[]
  }), [product.hasSerial]);

  const validationRules = useMemo(() => createValidationRules(product.hasSerial), [product.hasSerial]);

  const [formState, formActions] = useFormState(initialFormData, validationRules);

  useEffect(() => {
    if (product.hasSerial) {
      formActions.setField('quantity', 0);
      formActions.setField('serials', ['']);
    } else {
      formActions.setField('serials', []);
      formActions.setField('quantity', '');
    }
  }, [product.hasSerial]);

  const handleSubmit = useCallback(async (data: FormData) => {
    const validationError = validateFormSubmission(product, currentUserId);
    if (validationError) {
      showError(validationError);
      return;
    }

    const formData = prepareFormData(data, product, currentUserId);

    try {
      const success = await addStock(formData);

      if (success) {
        LoggerService.info('Stock added successfully', formData);
        showSuccess('تم إضافة المخزون بنجاح');
        onSuccess();
      } else {
        LoggerService.error('Failed to add stock', {formData, success});
        // If addStock returns false, it means the operation failed but didn't throw
        // This is a business logic error, not an API error
        showError('فشل في إضافة المخزون - يرجى المحاولة مرة أخرى');
      }
    } catch (error) {
      LoggerService.error('Error adding stock', error, 'AddStockForm');
      // Let StandardizedErrorHandler extract server error message
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  }, [product, currentUserId, addStock, onSuccess, showSuccess, showError]);

  return (
    <div className="space-y-6">
      <ProductInfoCard
        product={product}
        currentStock={currentStock}
        isLoading={stockLoading}
      />
      <LocationInfoCard />

      <Card className="p-6">
        <div className="mb-4">
          <AppCardTitle className="text-gray-800 font-semibold">إضافة المخزون</AppCardTitle>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
          {!product.hasSerial && (
            <FormField
              type="number"
              label="الكمية *"
              placeholder="أدخل الكمية"
              min={1}
              {...createFieldProps('quantity', formState, formActions)}
              disabled={submitting}
            />
          )}

          {product.hasSerial && (
            <UnifiedSerialNumberInput
              value={formState.data.serials}
              onChange={(serials) => formActions.setField('serials', serials)}
              error={formState.errors.serials}
              context="stock"
              required={false}
              showBulkInput={true}
              showCopyButton={true}
              showClearButton={true}
              showSerialCount={true}
              maxSerials={1000}
            />
          )}

          <FormActions
            onSubmit={() => formActions.submit(handleSubmit)}
            onCancel={onCancel}
            submitLabel={submitting ? 'جاري الإضافة...' : 'إضافة المخزون'}
            cancelLabel="إلغاء"
            isLoading={formState.isSubmitting || submitting}
            disabled={!formState.isValid}
          />
        </form>
      </Card>
    </div>
  );
};

export default AddStockForm;
